import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {GetServiceOrderIntegrationsMazdaPreauthorizationApiResponse} from '@dms/api/workshop';

import {MazdaClaimSendFormType} from '../types/MazdaClaimSendFormType';
import {toMazdaClaimMaintenanceAnswer} from './toMazdaClaimMaintenanceAnswer';

type MazdaPreauthorization = GetServiceOrderIntegrationsMazdaPreauthorizationApiResponse;

// A pre-authorization the dealer never filled in still answers with the server's own draft defaults,
// so applying it would only overwrite the claim. These fields prove a human saved something.
export const hasMazdaPreauthorizationData = (preauthorization: MazdaPreauthorization) =>
  isNotNilOrEmpty(preauthorization?.scope) ||
  isNotNilOrEmpty(preauthorization?.warrantyType) ||
  isNotNilOrEmpty(preauthorization?.causalPart) ||
  isNotNilOrEmpty(preauthorization?.specialCausalPart) ||
  isNotNilOrEmpty(preauthorization?.message);

/**
 * Only fields the pre-authorization actually holds are carried over — a nil one would blank the
 * claim's own check-in prefill. The claim's repairDate is never touched: the pre-authorization
 * contract has no such field.
 */
export const mapMazdaPreauthorizationToClaimValues = (
  preauthorization: MazdaPreauthorization
): Partial<MazdaClaimSendFormType> => {
  const maintenanceAnswer = toMazdaClaimMaintenanceAnswer(
    preauthorization?.vehicleServicedAccordingToManufacturerSpecs
  );
  const specialCausalPart = preauthorization?.specialCausalPart?.trim();

  return {
    ...(isNotNil(preauthorization?.scope) && {scope: preauthorization.scope}),
    ...(isNotNil(preauthorization?.warrantyType) && {
      warrantyType: preauthorization.warrantyType,
    }),
    ...(isNotNil(preauthorization?.mileage) && {mileage: preauthorization.mileage}),
    ...(isNotNil(maintenanceAnswer) && {
      vehicleServicedAccordingToManufacturerSpecs: maintenanceAnswer,
    }),
    ...(isNotNil(preauthorization?.installationDate) && {
      installationDate: preauthorization.installationDate,
    }),
    ...(isNotNil(preauthorization?.installationMileage) && {
      installationMileage: preauthorization.installationMileage,
    }),
    ...(isNotNil(preauthorization?.installationRepairNo) && {
      installationRepairNo: preauthorization.installationRepairNo,
    }),
    ...(isNotNil(preauthorization?.causalPart) && {causalPart: preauthorization.causalPart}),
    ...(isNotNilOrEmpty(specialCausalPart) && {specialCausalPart, isSpecialCausalPart: true}),
    ...(isNotNil(preauthorization?.message) && {message: preauthorization.message}),
  };
};
